import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActivityIndicator,
    Modal,
    Pressable,
    RefreshControl,
    ScrollView,
    StyleSheet,
    Text,
    View
} from "react-native";
import { RouteProp, useRoute } from "@react-navigation/native";

import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import {
    format,
    parse,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear
} from "date-fns";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { ConfirmDialog } from "../../../shared/ui/ConfirmDialog";
import { Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";

import { ChantingService } from "../../../services/ChantingService";
import { ChantingDto } from "../../../models/ChantingModels";
import { AuthStackParamList } from "../../../navigation/navigationTypes";
import { resolveHistoryRange } from "../../../shared/utils/historyNavigation";
import { useAuth } from "../../../../context/AuthContext";

type FilterType = "Weekly" | "Monthly" | "Quarterly" | "Yearly" | "Custom";
type ChantingHistoryRouteProp = RouteProp<AuthStackParamList, "ChantingHistory">;

export function ChantingHistoryScreen() {
    const { user } = useAuth();
    const route = useRoute<ChantingHistoryRouteProp>();


    const today = new Date();
    const initialRange = resolveHistoryRange(route.params, today);

    const [filterType, setFilterType] = useState<FilterType>(
        initialRange.hasNavigationDates ? "Custom" : "Monthly"
    );

    const effectiveUserId = useMemo(() => {
        return initialRange.devoteeId || user?.userId || "";
    }, [initialRange.devoteeId, user?.userId]);

    const [fromDate, setFromDate] = useState(initialRange.fromDate);
    const [toDate, setToDate] = useState(initialRange.toDate);

    const [entries, setEntries] = useState<any[]>([]);
    const [summary, setSummary] = useState({ rangeTotal: 0, rangeMarks: 0, isSelf: true });

    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    const [editing, setEditing] = useState<any | null>(null);

    const [b8, setB8] = useState("0");
    const [b12, setB12] = useState("0");
    const [b6, setB6] = useState("0");
    const [a6, setA6] = useState("0");

    const applyFilter = (type: FilterType) => {

        setFilterType(type);

        const now = new Date();

        let start = startOfMonth(now);
        let end = endOfMonth(now);

        if (type === "Weekly") {
            start = startOfWeek(now);
            end = endOfWeek(now);
        }

        if (type === "Quarterly") {
            start = startOfQuarter(now);
            end = endOfQuarter(now);
        }

        if (type === "Yearly") {
            start = startOfYear(now);
            end = endOfYear(now);
        }

        setFromDate(format(start, "yyyy-MM-dd"));
        setToDate(format(end, "yyyy-MM-dd"));

    };

    const openPicker = (mode: "from" | "to") => {

        const value = new Date(mode === "from" ? fromDate : toDate);

        DateTimePickerAndroid.open({

            value,
            mode: "date",

            onChange: (event, date) => {

                if (event.type !== "set" || !date) return;

                const v = format(date, "yyyy-MM-dd");

                if (mode === "from") setFromDate(v);
                else setToDate(v);

                setFilterType("Custom");

            }

        });

    };

    const loadData = useCallback(async () => {

        try {

            if (!effectiveUserId) return;
            setLoading(true);
            const res = await ChantingService.history({
                userId: effectiveUserId,
                fromDate,
                toDate
            });

            if (res?.status !== 200) {
                Flash.error(res?.message);
                return;
            }

            setEntries(res.data.records || []);
            setSummary(res.data.summary || { rangeTotal: 0, rangeMarks: 0 });

        }
        catch (e: any) {
            Flash.error(e?.message);
        }
        finally {
            setLoading(false);
        }

    }, [fromDate, toDate, effectiveUserId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const onRefresh = async () => {
        setRefreshing(true);
        try {
            await loadData();
        } finally {
            setRefreshing(false);
        }
    };

    const openEdit = async (id: string) => {

        try {

            setLoading(true);

            const res = await ChantingService.get(id);

            if (res?.status !== 200) {
                Flash.error(res?.message);
                return;
            }

            const row = res.data?.[0];

            setEditing(row);

            setB8(String(row.before8am));
            setB12(String(row.before12pm));
            setB6(String(row.before6pm));
            setA6(String(row.after6pm));

        }
        catch (e: any) {
            Flash.error(e?.message);
        }
        finally {
            setLoading(false);
        }

    };

    const update = async () => {

        if (!editing) return;

        const totalMala =
            (parseInt(b8) || 0) +
            (parseInt(b12) || 0) +
            (parseInt(b6) || 0) +
            (parseInt(a6) || 0);

        const totalMarks =
            (parseInt(b8) || 0) * 3 +
            (parseInt(b12) || 0) * 2 +
            (parseInt(b6) || 0) * 1 +
            (parseInt(a6) || 0) * 1;

        const payload: ChantingDto = {

            chantingId: editing.chantingId,
            date: editing.date,

            before8am: parseInt(b8) || 0,
            before12pm: parseInt(b12) || 0,
            before6pm: parseInt(b6) || 0,
            after6pm: parseInt(a6) || 0,

            totalMala,
            totalMarks

        };

        const res = await ChantingService.update(payload);

        if (res?.status !== 200) {
            Flash.error(res?.message);
            return;
        }

        Flash.success("Success!", "Chanting Updated Successfully!");

        setEditing(null);

        loadData();

    };

    const remove = (id: string) => {
        setPendingDeleteId(id);

    };

    const Chip = ({ text, active, onPress }: { text: string; active: boolean; onPress: () => void }) => (

        <Pressable
            onPress={onPress}
            style={[
                styles.chip,
                active ? styles.chipActive : styles.chipInactive
            ]}
        >
            <Text style={[styles.chipText, { color: active ? "#fff" : "#EA580C" }]}>
                {text}
            </Text>
        </Pressable>

    );

    return (

        <DivineBackground>

            <Header title="Chanting History" showBack showHome />

            <ScrollView
                contentContainerStyle={styles.page}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        colors={["#F97316"]}
                        tintColor="#F97316"
                    />
                }
            >

                <View style={styles.filterCard}>

                    <View style={styles.filterHead}>
                        <Text>🔎 </Text>
                        <Text style={styles.filterTitle}>Filter History</Text>
                    </View>

                    <ScrollView horizontal showsHorizontalScrollIndicator={false}>

                        <Chip text="Weekly" active={filterType === "Weekly"} onPress={() => applyFilter("Weekly")} />
                        <Chip text="Monthly" active={filterType === "Monthly"} onPress={() => applyFilter("Monthly")} />
                        <Chip text="Quarterly" active={filterType === "Quarterly"} onPress={() => applyFilter("Quarterly")} />
                        <Chip text="Yearly" active={filterType === "Yearly"} onPress={() => applyFilter("Yearly")} />

                    </ScrollView>

                    <View style={styles.dateRow}>

                        <Pressable onPress={() => openPicker("from")} style={styles.dateBox}>
                            <Text style={styles.calendarIcon}>📅</Text>
                            <Text style={styles.dateText}>{format(new Date(fromDate), "dd MMM yyyy")}</Text>
                        </Pressable>

                        <Pressable onPress={() => openPicker("to")} style={styles.dateBox}>
                            <Text style={styles.calendarIcon}>📅</Text>
                            <Text style={styles.dateText}>{format(new Date(toDate), "dd MMM yyyy")}</Text>
                        </Pressable>

                    </View>

                </View>

                <View style={styles.summaryRow}>

                    <View style={styles.summaryCard}>
                        <Text style={styles.summaryLabel}>Range Total</Text>
                        <Text style={styles.summaryValue}>{summary.rangeTotal}</Text>
                    </View>

                    <View style={styles.summaryCard}>
                        <Text style={styles.summaryLabel}>Range Marks</Text>
                        <Text style={styles.summaryValue}>{summary.rangeMarks}</Text>
                    </View>

                </View>

                {loading ? (
                    <ActivityIndicator />
                ) : entries.length === 0 ? (
                    <Text style={styles.empty}>No records found</Text>
                ) : (

                    entries.map((row) => (

                        <View key={row.data.chantingId} style={styles.itemCard}>

                            <View style={styles.itemHeader}>

                                <View>

                                    <Text style={styles.itemDate}>
                                        {format(parse(row.date, "dd-MM-yyyy", new Date()), "dd MMM yyyy")}
                                    </Text>

                                    <Text style={styles.itemDay}>
                                        {format(parse(row.date, "dd-MM-yyyy", new Date()), "EEEE")}
                                    </Text>

                                </View>

                                <View style={styles.marksBadge}>
                                    <Text style={styles.marksText}>{row.data.totalMarks} Marks</Text>
                                </View>

                            </View>

                            <View style={styles.statsRow}>

                                <View style={styles.statBox}>
                                    <Text style={styles.statValue}>{row.data.before8am}</Text>
                                    <Text style={styles.statLabel}>{"<8am"}</Text>
                                </View>

                                <View style={styles.statBox}>
                                    <Text style={styles.statValue}>{row.data.before12pm}</Text>
                                    <Text style={styles.statLabel}>{"<12pm"}</Text>
                                </View>

                                <View style={styles.statBox}>
                                    <Text style={styles.statValue}>{row.data.before6pm}</Text>
                                    <Text style={styles.statLabel}>{"<6pm"}</Text>
                                </View>

                                <View style={styles.statBox}>
                                    <Text style={styles.statValue}>{row.data.after6pm}</Text>
                                    <Text style={styles.statLabel}>{">6pm"}</Text>
                                </View>

                            </View>


                            {summary.isSelf && (
                                <View style={styles.actions}>

                                    {row.data.isEditable ? (

                                        <Pressable onPress={() => openEdit(row.data.chantingId)} style={styles.iconBtn} disabled={loading}>
                                            <Text>✏️</Text>
                                        </Pressable>
                                    ) : (
                                        <View style={[styles.iconBtn, styles.iconBtnPlaceholder]} />
                                    )}

                                    <Pressable onPress={() => remove(row.data.chantingId)} style={styles.iconBtn} disabled={loading}>
                                        <Text>🗑</Text>
                                    </Pressable>

                                </View>
                            )}

                        </View>

                    ))

                )}

            </ScrollView>

            <Modal
                transparent
                visible={!!editing}
                animationType="fade"
                onRequestClose={() => {
                    if (!loading) setEditing(null);
                }}
            >

                <View style={styles.modalOverlay}>

                    <View style={styles.modalCard}>

                        <Text style={styles.modalTitle}>Edit Entry</Text>

                        <Input label="< 8am" value={b8} onChangeText={setB8} />
                        <Input label="< 12pm" value={b12} onChangeText={setB12} />
                        <Input label="< 6pm" value={b6} onChangeText={setB6} />
                        <Input label="> 6pm" value={a6} onChangeText={setA6} />

                        <View style={styles.modalBtns}>

                            <Pressable style={styles.cancelBtn} onPress={() => setEditing(null)} disabled={loading}>
                                <Text style={styles.cancelText}>Cancel</Text>
                            </Pressable>

                            <Pressable style={[styles.updateBtn, loading && styles.buttonDisabled]} onPress={update} disabled={loading}>
                                <Text style={styles.updateText}>{loading ? "Updating..." : "Update"}</Text>
                            </Pressable>

                        </View>

                    </View>

                </View>

            </Modal>

            <ConfirmDialog
                visible={!!pendingDeleteId}
                title="Delete Entry"
                message="Are you sure you want to delete?"
                confirmText="Delete"
                destructive
                loading={loading}
                onCancel={() => setPendingDeleteId(null)}
                onConfirm={async () => {
                    if (!pendingDeleteId) return;
                    try {
                        setLoading(true);
                        const res = await ChantingService.delete(pendingDeleteId);

                        if (res?.status !== 200) {
                            Flash.error(res?.message);
                            return;
                        }

                        Flash.success("Success!", "Chanting Deleted Successfully!");
                        setPendingDeleteId(null);
                        loadData();
                    } finally {
                        setLoading(false);
                    }
                }}
            />

        </DivineBackground>

    );

}

const styles = StyleSheet.create({

    page: { padding: 16, gap: 12 },

    filterCard: {
        backgroundColor: "#fff",
        padding: 14,
        borderRadius: 14,
        gap: 10
    },

    filterHead: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 10 },
    filterTitle: { fontSize: 15, fontWeight: "900", color: "#EA580C" },

    chip: {
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        marginRight: 8
    },

    chipActive: { backgroundColor: "#EA580C" },
    chipInactive: { backgroundColor: "#FFE7D6" },

    chipText: { fontWeight: "800", fontSize: 12 },

    dateRow: { flexDirection: "row", gap: 10 },

    dateBox: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        padding: 10,
        backgroundColor: "#F3F4F6",
        borderRadius: 10
    },

    calendarIcon: { marginRight: 6 },

    dateText: { fontWeight: "800" },

    summaryRow: { flexDirection: "row", gap: 10 },

    summaryCard: {
        flex: 1,
        backgroundColor: "#fff",
        padding: 14,
        borderRadius: 12
    },

    summaryLabel: { fontSize: 11, color: "#666" },
    summaryValue: { fontSize: 20, fontWeight: "900", color: "#EA580C" },

    itemCard: {
        backgroundColor: "#fff",
        padding: 14,
        borderRadius: 12,
        gap: 10
    },

    itemHeader: {
        flexDirection: "row",
        justifyContent: "space-between"
    },

    itemDate: { fontWeight: "900" },
    itemDay: { fontSize: 11, color: "#888" },

    marksBadge: {
        backgroundColor: "#FFE7D6",
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 8
    },

    marksText: {
        color: "#EA580C",
        fontWeight: "800"
    },

    statsRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        backgroundColor: "#F3F4F6",
        borderRadius: 10,
        paddingVertical: 10,
        marginTop: 6
    },

    statBox: {
        flex: 1,
        alignItems: "center"
    },

    statValue: {
        fontSize: 16,
        fontWeight: "900",
        color: "#111827"
    },

    statLabel: {
        fontSize: 11,
        color: "#6B7280",
        marginTop: 2
    },

    actions: {
        flexDirection: "row",
        justifyContent: "flex-end",
        gap: 10
    },

    iconBtn: {
        width: 36,
        height: 36,
        borderRadius: 10,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(249,115,22,0.12)"
    },
    iconBtnPlaceholder: {
        opacity: 0,
    },
    buttonDisabled: {
        opacity: 0.6,
    },

    empty: { textAlign: "center", marginTop: 30, color: "#999" },

    modalOverlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.4)",
        justifyContent: "center",
        padding: 20
    },

    modalCard: {
        backgroundColor: "#fff",
        borderRadius: 14,
        padding: 16,
        gap: 10
    },

    modalTitle: {
        fontWeight: "900",
        fontSize: 16
    },

    modalBtns: {
        flexDirection: "row",
        justifyContent: "flex-end",
        gap: 10,
        marginTop: 10
    },

    cancelBtn: {
        paddingHorizontal: 18,
        paddingVertical: 10,
        borderRadius: 10,
        backgroundColor: "#E5E7EB"
    },

    updateBtn: {
        paddingHorizontal: 18,
        paddingVertical: 10,
        borderRadius: 10,
        backgroundColor: "#EA580C"
    },

    updateText: {
        color: "#fff",
        fontWeight: "800"
    },

    cancelText: {
        fontWeight: "800"
    }

});
